import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation } from 'react-query';
import Layout from 'components/common/layout/Layout';
import CheckPassword from './CheckPassword';
import LabelInput from 'components/common/forms/LabelInput';
import SingleBtn from 'components/common/elements/SingleBtn';
import styles from 'styles/pages/mypage/ChangePassword.module.scss';
import { ROUTE_PATH } from 'constants/routePath';
import { ResetPasswordFormDefaultValues } from 'constants/auth';
import { TPasswordChangeByTokenParams } from 'types/apis/auth';
import { PasswordChangeFormFields, TPasswordChangeForm } from 'types/form/auth';
import { postPasswordChangeByToken } from 'apis/auth';
import { isTCustomAxiosError } from 'apis/config/axios';
import useModal from 'hooks/useModal';

const ChangePassword = () => {
  const { openModal } = useModal();
  const navigate = useNavigate();

  const [passwordCheck, setPasswordCheck] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const methods = useForm<TPasswordChangeForm>({
    defaultValues: ResetPasswordFormDefaultValues,
  });
  const {
    handleSubmit,
    control,
    formState: { errors: formErrors },
  } = methods;
  const firstFormErrorKey = Object.keys(
    formErrors
  )[0] as keyof TPasswordChangeByTokenParams;

  const [newPassword, newPasswordCheck] = useWatch({
    control,
    name: [
      PasswordChangeFormFields.NewPassword,
      PasswordChangeFormFields.NewPasswordCheck,
    ],
  });

  const goToHome = () => navigate(ROUTE_PATH.common.HOME);

  const { mutate: passwordChangeByTokenMutation, isLoading } = useMutation(
    'postPasswordChangeByToken',
    postPasswordChangeByToken,
    {
      onSuccess: () =>
        openModal({
          title: '비밀번호 변경 완료',
          text: '비밀번호 변경 완료되었습니다.',
          onSubmit: goToHome,
          onClickOutside: goToHome,
        }),
      onError: (error) => {
        if (isTCustomAxiosError(error)) {
          if (error.code === 409) {
            setErrorMsg('이전 비밀번호와 동일한 비밀번호입니다.');
          }
        }
      },
    }
  );

  const onValid = (data: TPasswordChangeByTokenParams) => {
    setErrorMsg('');
    passwordChangeByTokenMutation({
      newPassword: data.newPassword,
      newPasswordCheck: data.newPasswordCheck,
    });
  };

  useEffect(() => {
    if (errorMsg && newPassword !== newPasswordCheck) {
      setErrorMsg('');
    }
  }, [newPassword, newPasswordCheck]);

  return (
    <Layout>
      <h1 className="main_title mt40">비밀번호 변경</h1>
      {!passwordCheck ? (
        <>
          <CheckPassword
            isChangePW={true}
            setPasswordCheck={setPasswordCheck}
          />
        </>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onValid)}>
            <p className="mt4 lh24">
              이전 비밀번호와 다른 비밀번호로 새 비밀번호를 입력해 주세요.
              <br /> 공백없이 영문, 숫자, 특수문자를 포함한 8~16자리를 입력해
              주세요.
            </p>
            <div className={styles.wrapper}>
              <div className={styles.input_wrapper}>
                <LabelInput
                  labelText="새 비밀번호"
                  inputType="password"
                  placeholderText="새 비밀번호를 입력해 주세요."
                  name={PasswordChangeFormFields.NewPassword}
                  required={true}
                  rules={{
                    required: '비밀번호를 입력해 주세요.',
                    minLength: {
                      value: 8,
                      message: '비밀번호는 최소 8자 이상이어야 합니다.',
                    },
                    maxLength: {
                      value: 16,
                      message: '비밀번호는 최대 16자까지 가능합니다.',
                    },
                  }}
                />

                <LabelInput
                  labelText="비밀번호 확인"
                  inputType="password"
                  placeholderText="비밀번호를 재입력해 주세요."
                  name={PasswordChangeFormFields.NewPasswordCheck}
                  required={true}
                  rules={{
                    required: '비밀번호를 입력해 주세요.',
                    validate: (value) => {
                      return (
                        value === newPassword ||
                        '비밀번호가 일치하지 않습니다. 다시 한번 확인해 주세요.'
                      );
                    },
                  }}
                />
              </div>

              <div style={{ height: '22px' }}>
                {errorMsg && <p className={styles.error_msg}>{errorMsg}</p>}
                {firstFormErrorKey && (
                  <p className={styles.error_msg}>
                    {formErrors[firstFormErrorKey]?.message}
                  </p>
                )}
              </div>

              <SingleBtn
                type="submit"
                btnText="비밀번호 변경"
                isBtnActive={true}
                btnStyle={{
                  width: '100%',
                  marginTop: '26px',
                }}
                disabled={isLoading}
              />
            </div>
          </form>
        </FormProvider>
      )}
    </Layout>
  );
};

export default ChangePassword;
